const ANONYMOUS_USER_KEY = 'dash_web_user_id';
const ANONYMOUS_SESSION_KEY = 'dash_web_session_id';
const ANONYMOUS_ACTIVITY_KEY = 'dash_web_session_last_activity';
const AUTH_PROMPT_DISMISSED_KEY = 'dash_phone_auth_prompt_dismissed';
const ONBOARDING_SEEN_KEY = 'dash_onboarding_seen';
const AUTHENTICATED_SESSION_PREFIX = 'dash_phone_chat_session_';
const AUTHENTICATED_ACTIVITY_PREFIX = 'dash_phone_chat_last_activity_';
export const FRESH_CHAT_RESET_EVENT = 'dash:fresh-chat-reset';
export const FRESH_CHAT_RESET_STORAGE_KEY = 'dash_fresh_chat_reset';
// Sentinel "thread id" for the client-only Add-Vehicle sidebar tab. Selecting it shows the
// Add-Vehicle card (not a real conversation), so it must be treated as NOT a real thread
// everywhere history/threads are loaded.
export const ADD_VEHICLE_TAB_ID = '__add_vehicle_tab__';
// Fired by the card's ✕ / successful submit to remove the Add-Vehicle tab from the sidebar.
export const CLOSE_ADD_VEHICLE_TAB_EVENT = 'dash:close-add-vehicle-tab';

// The ONE message shown whenever a signed-in session stops being valid, wherever that is
// noticed (a 401 on any secure call, or the chat stream refusing). Kept here so the chat and
// the layout can't drift into saying different things about the same event, and worded for a
// person rather than for us: no "session id", no "verify", no status codes.
export const SESSION_EXPIRED_MESSAGE = "You've been signed out. Please log in to continue.";

const CHAT_SESSION_TIMEOUT_MS = 24 * 60 * 60 * 1000;

function createRandomSuffix() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID().replace(/-/g, '').slice(0, 12);
    }
    return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

export function getApiBaseUrl() {
    const configuredBaseUrl = (process.env.REACT_APP_API_URL || '').trim();

    if (typeof window === 'undefined') {
        return configuredBaseUrl.replace(/\/$/, '');
    }

    const currentHostname = window.location.hostname;
    const isCurrentHostLocal = currentHostname === 'localhost' || currentHostname === '127.0.0.1';

    if (!configuredBaseUrl) {
        // Use relative URLs so the CRA proxy (setupProxy.js → REACT_APP_API_TARGET) handles routing
        return '';
    }

    try {
        const parsedUrl = new URL(configuredBaseUrl);
        const isConfiguredHostLocal = parsedUrl.hostname === 'localhost' || parsedUrl.hostname === '127.0.0.1';

        // Never let a deployed site call the visitor's localhost.
        if (!isCurrentHostLocal && isConfiguredHostLocal) {
            return '';
        }
    } catch (_) {
        return configuredBaseUrl.replace(/\/$/, '');
    }

    return configuredBaseUrl.replace(/\/$/, '');
}

export function getUserDetailsBaseUrl() {
    const configuredBaseUrl = (process.env.REACT_APP_USER_DETAILS_API_URL || '').trim();

    if (typeof window === 'undefined') {
        return configuredBaseUrl.replace(/\/$/, '');
    }

    const currentHostname = window.location.hostname;
    const isCurrentHostLocal = currentHostname === 'localhost' || currentHostname === '127.0.0.1';

    if (!configuredBaseUrl) {
        // In local dev, return '' (relative URL) so requests go through the CRA proxy
        // which routes user_details paths to port 8001 - avoids CORS issues entirely.
        return '';
    }

    try {
        const parsedUrl = new URL(configuredBaseUrl);
        const isConfiguredHostLocal = parsedUrl.hostname === 'localhost' || parsedUrl.hostname === '127.0.0.1';

        // Never let a deployed site call the visitor's localhost.
        if (!isCurrentHostLocal && isConfiguredHostLocal) {
            return '';
        }
    } catch (_) {
        return configuredBaseUrl.replace(/\/$/, '');
    }

    return configuredBaseUrl.replace(/\/$/, '');
}

export function getOrCreateAnonymousId() {
    const saved = localStorage.getItem(ANONYMOUS_USER_KEY);
    if (saved) return saved;

    const id = `web_${createRandomSuffix()}`;
    localStorage.setItem(ANONYMOUS_USER_KEY, id);
    return id;
}

export function getOrCreateAnonymousSessionId() {
    const savedSession = localStorage.getItem(ANONYMOUS_SESSION_KEY);
    const lastActivity = parseInt(localStorage.getItem(ANONYMOUS_ACTIVITY_KEY) || '0', 10);
    const now = Date.now();

    if (savedSession && (now - lastActivity) < CHAT_SESSION_TIMEOUT_MS) {
        return savedSession;
    }

    const newSessionId = `web_sess_${createRandomSuffix()}`;
    localStorage.setItem(ANONYMOUS_SESSION_KEY, newSessionId);
    localStorage.setItem(ANONYMOUS_ACTIVITY_KEY, String(now));
    return newSessionId;
}

export function resetAnonymousChatSession() {
    localStorage.removeItem(ANONYMOUS_SESSION_KEY);
    localStorage.removeItem(ANONYMOUS_ACTIVITY_KEY);
}

export function refreshAnonymousSessionActivity() {
    localStorage.setItem(ANONYMOUS_ACTIVITY_KEY, String(Date.now()));
}

export function getAuthenticatedChatSessionId(phone) {
    const normalizedPhone = normalizePhoneForStorage(phone);
    const sessionStorageKey = `${AUTHENTICATED_SESSION_PREFIX}${normalizedPhone}`;
    const activityStorageKey = `${AUTHENTICATED_ACTIVITY_PREFIX}${normalizedPhone}`;
    const saved = localStorage.getItem(sessionStorageKey);
    const lastActivity = parseInt(localStorage.getItem(activityStorageKey) || '0', 10);
    const now = Date.now();

    if (saved && (now - lastActivity) < CHAT_SESSION_TIMEOUT_MS) {
        return saved;
    }

    const sessionId = `web_phone_${normalizedPhone}_${createRandomSuffix()}`;
    localStorage.setItem(sessionStorageKey, sessionId);
    localStorage.setItem(activityStorageKey, String(now));
    return sessionId;
}

export function resetAuthenticatedChatSession(phone) {
    const normalizedPhone = normalizePhoneForStorage(phone);
    const sessionStorageKey = `${AUTHENTICATED_SESSION_PREFIX}${normalizedPhone}`;
    const activityStorageKey = `${AUTHENTICATED_ACTIVITY_PREFIX}${normalizedPhone}`;
    localStorage.removeItem(sessionStorageKey);
    localStorage.removeItem(activityStorageKey);
}

export function refreshAuthenticatedSessionActivity(phone) {
    const normalizedPhone = normalizePhoneForStorage(phone);
    const activityStorageKey = `${AUTHENTICATED_ACTIVITY_PREFIX}${normalizedPhone}`;
    localStorage.setItem(activityStorageKey, String(Date.now()));
}

export function isAuthPromptDismissed() {
    return localStorage.getItem(AUTH_PROMPT_DISMISSED_KEY) === '1';
}

export function setAuthPromptDismissed(dismissed) {
    if (dismissed) {
        localStorage.setItem(AUTH_PROMPT_DISMISSED_KEY, '1');
        return;
    }
    localStorage.removeItem(AUTH_PROMPT_DISMISSED_KEY);
}

export function isOnboardingSeen() {
    // Original behavior:
    return localStorage.getItem(ONBOARDING_SEEN_KEY) === '1';

    // Temporary: force onboarding open for anonymous visitors while testing the
    // mobile carousel. Restore the original localStorage check before release.
    // return true;
}

export function setOnboardingSeen() {
    localStorage.setItem(ONBOARDING_SEEN_KEY, '1');
}

function getBrowserStorageAreas() {
    const areas = [];
    try {
        if (typeof localStorage !== 'undefined') areas.push(localStorage);
    } catch {}
    try {
        if (typeof sessionStorage !== 'undefined') areas.push(sessionStorage);
    } catch {}
    return areas;
}

export function eraseAllSessionData() {
    getBrowserStorageAreas().forEach((storage) => {
        try {
            Object.keys(storage)
                .filter(k => k.startsWith('dash_'))
                .forEach(k => storage.removeItem(k));
        } catch {}
    });
}

export function notifyChatReset(reason = 'fresh-chat', options = {}) {
    const shouldPersist = options.persist !== false;
    try {
        if (shouldPersist) {
            sessionStorage.setItem(FRESH_CHAT_RESET_STORAGE_KEY, String(Date.now()));
        }
        window.dispatchEvent(new CustomEvent(FRESH_CHAT_RESET_EVENT, { detail: { reason } }));
    } catch {}
}

export function normalizePhoneForStorage(phone) {
    const digitsOnly = String(phone || '').replace(/\D/g, '');
    return digitsOnly || 'unknown';
}

const COUNTRY_CALLING_CODES = [
    '1', '7', '20', '27', '30', '31', '32', '33', '34', '36', '39', '40', '41',
    '43', '44', '45', '46', '47', '48', '49', '51', '52', '53', '54', '55', '56',
    '57', '58', '60', '61', '62', '63', '64', '65', '66', '81', '82', '84', '86',
    '90', '91', '92', '93', '94', '95', '98', '211', '212', '213', '216', '218',
    '220', '221', '222', '223', '224', '225', '226', '227', '228', '229', '230',
    '231', '232', '233', '234', '235', '236', '237', '238', '239', '240', '241',
    '242', '243', '244', '245', '246', '248', '249', '250', '251', '252', '253',
    '254', '255', '256', '257', '258', '260', '261', '262', '263', '264', '265',
    '266', '267', '268', '269', '290', '291', '297', '298', '299', '350', '351',
    '352', '353', '354', '355', '356', '357', '358', '359', '370', '371', '372',
    '373', '374', '375', '376', '377', '378', '380', '381', '382', '383', '385',
    '386', '387', '389', '420', '421', '423', '500', '501', '502', '503', '504',
    '505', '506', '507', '508', '509', '590', '591', '592', '593', '594', '595',
    '596', '597', '598', '599', '670', '672', '673', '674', '675', '676', '677',
    '678', '679', '680', '681', '682', '683', '685', '686', '687', '688', '689',
    '690', '691', '692', '850', '852', '853', '855', '856', '880', '886', '960',
    '961', '962', '963', '964', '965', '966', '967', '968', '970', '971', '972',
    '973', '974', '975', '976', '977', '992', '993', '994', '995', '996', '998',
].sort((a, b) => b.length - a.length);

export function getPhoneDisplayParts(phone, country = null) {
    const raw = String(phone || '').trim();
    const digits = raw.replace(/\D/g, '');

    if (!digits) {
        // No phone at all is a real state now that accounts can be email-only. Return
        // empty rather than the old 'Phone verified' placeholder, which showed that
        // literal string to email users. Callers pick their own fallback — see
        // formatAccountLabel below.
        return {
            countryCode: '',
            nationalNumber: raw,
            label: raw,
        };
    }

    const selectedDialCode = String(country?.dialCode || '').replace(/\D/g, '');
    let dialCode = selectedDialCode && digits.startsWith(selectedDialCode) && digits.length > selectedDialCode.length
        ? selectedDialCode
        : '';

    const shouldInferDialCode = raw.startsWith('+') || digits.length > 10;

    if (!dialCode && shouldInferDialCode) {
        dialCode = COUNTRY_CALLING_CODES.find((code) => (
            digits.startsWith(code) && digits.length > code.length
        )) || '';
    }

    if (!dialCode && digits.length > 10) {
        dialCode = digits.slice(0, digits.length - 10);
    }

    const nationalNumber = dialCode && digits.startsWith(dialCode)
        ? digits.slice(dialCode.length)
        : digits;
    const countryCode = dialCode ? `+${dialCode}` : '';
    // US/Canada (+1): group the 10-digit national number as 3-3-4 ("+1 908-507-0021", #421).
    // Other countries keep the national block intact so they aren't mis-grouped. nationalNumber
    // stays raw digits for callers that use it for logic — only the display label is grouped.
    const displayNational = (dialCode === '1' && nationalNumber.length === 10)
        ? `${nationalNumber.slice(0, 3)}-${nationalNumber.slice(3, 6)}-${nationalNumber.slice(6)}`
        : nationalNumber;
    const label = countryCode ? `${countryCode} ${displayNational}` : displayNational;

    return {
        countryCode,
        nationalNumber,
        label,
    };
}

export function formatPhoneLabel(phone) {
    return getPhoneDisplayParts(phone).label;
}

// How an account is named in the UI when it may be identified by phone, email, or
// both. Phone first (that is what most accounts have), then email, then a neutral
// label — never a status string like "Phone verified" pretending to be an identifier.
export function formatAccountLabel(phone, email) {
    return formatPhoneLabel(phone) || String(email || '').trim() || 'CarFido user';
}

export function getTesterModeHeaders() {
    try {
        if (localStorage.getItem('TesterMode') === 'yes') {
            return { 'X-Tester-Mode': 'yes' };
        }
    } catch (_) {}
    return {};
}

// Thread persistence

const THREAD_KEY_PREFIX = 'dash_current_thread_';

export function getStoredThreadId(phone) {
    const key = `${THREAD_KEY_PREFIX}${normalizePhoneForStorage(phone)}`;
    const val = localStorage.getItem(key);
    return val ? parseInt(val, 10) : null;
}

export function setStoredThreadId(phone, threadId) {
    const key = `${THREAD_KEY_PREFIX}${normalizePhoneForStorage(phone)}`;
    if (threadId == null) {
        localStorage.removeItem(key);
    } else {
        localStorage.setItem(key, String(threadId));
    }
}

export function clearStoredThreadId(phone) {
    const key = `${THREAD_KEY_PREFIX}${normalizePhoneForStorage(phone)}`;
    localStorage.removeItem(key);
}
